import React from "react";
import _ from "lodash";
import emojiRegex from "emoji-regex";
import showdown from "showdown";

// DS
import { CopyOutlined } from "@toddle-edu/ds-icons";

import {
  WRITING,
  STREAMING,
  BROWSING_THE_INTERNET,
  ANALYSING,
  ADDING_TO_UNIT,
  GENERATING,
  THINKING,
  TOOL_INDICATION,
  GATHERING_CURRICULUM_INFORMATION,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_DP,
  ADDING_TO_UNIT_FLOW,
  SEARCHING,
  MYP_INTERDISCIPLINARY,
  PYP_TRANSDISCIPLINARY,
  UBD_INQUIRY,
} from "Constants/stringConstants";
import {
  extractContentFromHTML,
  getStyleStrippedText,
  htmlToText,
  generateRandomId,
  getMemoizedLangFunction,
} from "Utils";
import { getNestedChildren } from "Utils/EntityFlowUtils";
import { checkEmptyElementValue } from "modules/ElementValueUtils";
import ACLStore from "lib/aclStore";
import { ENTITY_TYPE, SUBJECT_REQUIREMENT_PERM } from "./Constants";
import { getIsCourseBasedGradingEnabled } from "LearningCourses/modules/Utils";
import { getUnitPlanDetailsValueFromFieldKey } from "IBPlanner/modules/IBPlannerModule";
import update from "immutability-helper";
import { getElementValue } from "UnitPlans/modules/Utils";

// Regex pattern constants
export const REGEX_PATTERNS = {
  // List patterns
  LIST_ITEM: /^(\s*)(-|\*|\d+\.)\s+/,
  LIST_ITEM_WITH_BRACKET_NUMBER: /^(\s*)(-|\*|\d+\.)\s+(?:\[(\d+)\])?\s*(.*)/,
  LIST_ITEM_WITH_PARENTHESIS_NUMBER: /^(\s*)(-|\*|\d+\.)\s+(.*?)(?:\((\d+)\))?(.*)/,
  LIST_ITEM_WITH_TEXT_NUMBER: /^(\s*)(-|\*|\d+\.)\s+(.*?)(\d+)?\s*(.*)/,
  // Flexible number pattern - will find numbers in different formats anywhere in the line
  FLEXIBLE_NUMBER_PATTERN: /^(\s*)(-|\*|\d+\.)\s+(.*?)(?:\[(\d+)\]|\((\d+)\)|(\d+)(?=\s|$))(?:\s+(.*))?$/,

  // Indentation pattern
  INDENTATION: /^(\s*)/,

  // Number formatting patterns
  LARGE_NUMBERS: /\b\d{7,}\b/g,
  BRACKET_NUMBERS: /\[\d+\]/g,
  PARENTHESIS_NUMBERS: /\(\d+\)/g,

  // Other patterns
  LIST_VIEW_PATTERN: /^\s*(-|\d+\.?)(\s+)/,
  ACCORDION_VIEW_PATTERN: /^\s*(\*\s*)?\[([A-Z0-9_ ]+)\](?!\()/i,
  FIELD_VIEW_PATTERN: /^```/,
  HTML_TAG_PATTERN: /<\/?[a-z][\s\S]*>/i,
};

// some fields don't have a container of their own
// when scrolling to field in AI, we need to scroll to
// their enclosing or closest fields
export const FIELD_SCROLL_MAPPING = {
  conceptsQuestion: "concepts",
  actionQuestion: "action",
};

export const getEmojisAndTextFromString = string => {
  const regex = emojiRegex();
  const emojis = string.match(regex) || [];

  const textWithoutEmojis = _.find(
    _.split(string, regex),
    str => !_.isEmpty(_.trim(str))
  );

  return {
    emoji: _.find(emojis, emoji => !_.isEmpty(emoji)),
    text: _.trim(textWithoutEmojis),
  };
};

export const getIsAIResponseLoading = state => {
  return [
    ANALYSING,
    WRITING,
    STREAMING,
    BROWSING_THE_INTERNET,
    ADDING_TO_UNIT,
    ADDING_TO_UNIT_FLOW,
    SEARCHING,
    GENERATING,
    THINKING,
    TOOL_INDICATION,
    GATHERING_CURRICULUM_INFORMATION,
  ].includes(state);
};

export const getContentToSend = params => {
  const { t, selectedAction, isEnglishLanguageSelected, query } = params;

  const parsedQuery = _.trim(
    getStyleStrippedText({
      text: query,
      type: "SANITIZE_DANGER_HTML_PARAGRAPH",
    })
  );

  let content = t(selectedAction?.displayTextLocale);
  let shouldRemoveHTML = false;

  // 1. get the content which user has written in "Add addition instructions..." section
  let userInstructions = "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(query, "text/html");
  userInstructions = doc.querySelector("#additional-instructions")?.textContent;

  // 2. in AI tutor we can't set content = selectedAction?.displayText because user can modify the predefined prompt
  // also remove the additional instructions from the content, as we have already stored it in userInstructions
  if (parsedQuery) {
    if (!_.isEmpty(userInstructions)) {
      const tempElement = document.createElement("div");
      tempElement.innerHTML = query;

      const userInstructionsElement = tempElement.querySelector(
        "#additional-instructions"
      );

      if (userInstructionsElement) userInstructionsElement.remove();

      content = tempElement.innerHTML;
      shouldRemoveHTML = true;
    } else {
      content = _.cloneDeep(parsedQuery); // in AI tutor user can modify suggestion prompt
    }
  } else if (
    isEnglishLanguageSelected ||
    _.isNil(selectedAction?.displayTextLocale)
  ) {
    content = selectedAction?.displayText;
  }

  if (!shouldRemoveHTML) {
    const contentEditables = doc.querySelectorAll(
      "#content-editable-placeholder"
    );

    if (_.size(contentEditables) > 0) {
      shouldRemoveHTML = true;
    }
  }

  content = shouldRemoveHTML
    ? htmlToText(content, { wordwrap: null })
    : content;

  if (userInstructions) {
    content += ` ${userInstructions}`;
  }

  return _.trim(content);
};

export const getInputForAIResponse = params => {
  const {
    selectedAction,
    entityType,
    aiConversationId,
    content,
    unitPlanId,
    curriculumProgramId,
    isUnitFlow,
    unitPlanGrades,
    unitPlanSubjects,
    courseSubjectIds,
    isButtonClicked,
    conversationAttachments,
    fieldLocaleMapping,
    fieldTypeMapping,
    isCourseBasedGrading,
    gradesForMetadata,
    learningCourseId,
    parallelToolCalls,
    schoolInstructions,
  } = params || {};

  const unitPlanSubjectIds = _.map(unitPlanSubjects, "id");

  const metadata = {
    conversationId: aiConversationId,
    gradeIds: gradesForMetadata,
    subjectIds: !_.isEmpty(unitPlanSubjectIds)
      ? unitPlanSubjectIds
      : courseSubjectIds,
    isButtonClicked,
    fieldLocaleMapping,
    fieldTypeMapping,
    shouldSkipMutation: !isUnitFlow,
    isCourseBasedGrading,
    learningCourseId,
  };

  const gradeNames = _.join(_.map(unitPlanGrades, "name"), ", ");
  const subjectNames = _.join(_.map(unitPlanSubjects, "name"), ", ");

  return {
    // entityType,
    // context,
    action: _.get(selectedAction, "action", null),
    fieldType: _.get(selectedAction, "fieldType", null),
    entityId: unitPlanId,
    entityType: "UNIT_PLAN",
    context: {
      inputText: content,
      unitPlanId,
      grades: gradeNames,
      subjects: subjectNames,
      schoolInstructions,
    },
    parallelToolCalls,
    metadata,
    curriculumProgramId,
    shouldProcessAttachments: !_.isEmpty(conversationAttachments),
    ...(isUnitFlow ? { type: "UNIT_FLOW_WORKFLOW" } : {}),
  };
};

export const getBlockActions = ({ content: _content, isWritingMessage }) => {
  const { output: content } = extractStateAndContent(_content) || {};
  const actions = [];

  // Check if content contains [FOLDER] pattern
  // if (content && content.includes("[FOLDER]")) {
  // actions.push({
  //   key: "addToUnitFlow",
  //   label: "unitPlan:add_to_unit_flow",
  //   icon: <ArrowLeftPaneOutlined />,
  //   type: "SEND_MESSAGE",
  //   // TODO: Localise
  //   message: "Yes, please add it to the unit flow.",
  //   disabled: isWritingMessage,
  // });
  // }

  // Check if content contains **UID:<text>** pattern
  // if (content && content.includes("**UID:")) {
  // actions.push({
  //   key: "addToUnit",
  //   label: "unitPlan:add_to_unit",
  //   icon: <ArrowLeftPaneOutlined />,
  //   type: "SEND_MESSAGE",
  //   // TODO: Localise
  //   message: "Please add it to the unit.",
  //   disabled: isWritingMessage,
  // });
  // }

  actions.push({
    key: "copy",
    label: "common:copy",
    icon: <CopyOutlined />,
  });

  return actions;
};

const DEFAULT_TAG = "none";
/**
 * Extracts content from streamed text by grouping text under tag names.
 * Any text enclosed by complete tags is placed under that tag.
 * Text outside any tag is grouped under "none".
 * Incomplete (partial) tags at the beginning or end are discarded.
 *
 * @param {string} inputText Streamed text input.
 * @returns {Object} Object mapping tag names to their accumulated text.
 */
export const extractStateAndContent = inputText => {
  if (!inputText) return { [DEFAULT_TAG]: "" };

  // remove the line from inputText if it has `functions.` text in it
  inputText = inputText.replace(/^.*functions\..*(\r?\n|$)/gm, "");

  // remove the line if it has any of the tool calls
  inputText = inputText.replace(
    /^.*(get_planner_element_list|set_template_fields|commit_final_unit_flow_to_unit_plan).*(\r?\n|$)/gm,
    ""
  );

  // remove line if it has `multi_tool_use.parallel` text in it
  inputText = inputText.replace(/^.*multi_tool_use.*(\r?\n|$)/gm, "");

  const result = { [DEFAULT_TAG]: "" };
  let currentTag = DEFAULT_TAG;

  // Regular expression to match complete tags with a named capturing group "tag"
  const tagRegex = /<\/?(?<tag>\w+)[^>]*>/g;
  let lastIndex = 0;
  let match;

  // Iterate over all complete tag matches.
  while ((match = tagRegex.exec(inputText)) !== null) {
    const index = match.index;
    // Append text before the complete tag to the current tag's content.
    const textChunk = inputText.slice(lastIndex, index);
    if (!_.isEmpty(textChunk) && textChunk !== "\n") {
      result[currentTag] += textChunk;
    }

    const fullTag = match[0];
    const tagName = _.toLower(match.groups.tag);

    if (fullTag.startsWith("</")) {
      // A closing tag resets to default "none" group.
      currentTag = DEFAULT_TAG;
    } else {
      // An opening tag switches current grouping to the tag name.
      currentTag = tagName;
      if (!result[currentTag]) {
        result[currentTag] = "";
      }
    }
    lastIndex = tagRegex.lastIndex;
  }

  // Append any remaining text after the last complete tag.
  let remaining = inputText.slice(lastIndex);
  // Remove any partial tag at the end.
  const partialTagMatch = remaining.match(/<[^>]*$/);
  if (partialTagMatch) {
    remaining = remaining.slice(0, partialTagMatch.index);
  }
  result[currentTag] = _.trim((result[currentTag] || "") + remaining);

  // Remove any partial tag fragments from the beginning of every group.
  _.forEach(result, (val, key) => {
    // If the value starts with '<' and doesn't have a closing '>', it's a partial tag.
    if (_.startsWith(val, "<") && val.indexOf(">") === -1) {
      result[key] = "";
    } else {
      result[key] = _.trim(val);
    }
  });

  return result;
};

export const checkIsThinkingState = messages => {
  if (_.isEmpty(messages)) return false;
  const lastMessageContent = _.get(_.last(messages), "node.content");

  const { content, output } = extractStateAndContent(lastMessageContent);
  const isContentEmpty = !(content || output);

  const thinkingOpenTags = lastMessageContent.match(/<thinking>|<thi/g) || [];
  const thinkingCloseTags = lastMessageContent.match(/<\/thinking>/g) || [];
  const isUnclosedThinkingBlock =
    thinkingOpenTags.length > thinkingCloseTags.length;

  return isContentEmpty || isUnclosedThinkingBlock;
};

export const convertMarkdownToHTML = ({
  content,
  shouldParseContent = false,
}) => {
  const converter = new showdown.Converter({
    tables: true,
    // disableForced4SpacesIndentedSublists is used to disable the 4 spaces indentation for lists
    // setting this to true allows us to use 2 spaces indentation for lists
    disableForced4SpacesIndentedSublists: true,
  });

  if (shouldParseContent) {
    const parsedContent = _.replace(content, /###/g, "@@@")?.trim();
    const covertedText = converter.makeHtml(parsedContent);

    return _.replace(covertedText, /@@@/g, "###");
  }

  return converter.makeHtml(content);
};

// for RTE, we use heading from level 3 to 5
// so we need to replace h1 with h3, h2 with h4 and h3 with h5
const replaceHTMLHeadings = htmlString => {
  if (!htmlString) return htmlString;

  const mapping = { 1: "3", 2: "4", 3: "5" };
  // Match both opening and closing tags of h1, h2, h3
  return htmlString.replace(
    /<(\/?)h([1-3])([^>]*)>/gi,
    (match, slash, level, rest) => {
      const newLevel = mapping[level];
      return `<${slash}h${newLevel}${rest}>`;
    }
  );
};

/**
 * Determines the block type based on content pattern
 * @param {string} content - Line of content to analyze
 * @returns {string} Block type identifier
 */
const getBlockTypeFromContent = content => {
  if (REGEX_PATTERNS.FIELD_VIEW_PATTERN.test(content)) {
    return "FieldView";
  }
  if (REGEX_PATTERNS.LIST_VIEW_PATTERN.test(content)) {
    return "ListView";
  }
  if (REGEX_PATTERNS.ACCORDION_VIEW_PATTERN.test(content)) {
    return "AccordionView";
  }
  if (REGEX_PATTERNS.HTML_TAG_PATTERN.test(content)) {
    return "HTMLView";
  }
  return "MarkdownView";
};

/**
 * Parses accordion-style content and converts it to a structured format for NestedAccordion
 * @param {Array<string>} content - Array of content lines
 * @returns {Object} Object containing parsed items and title
 */
export const parseAccordionContent = content => {
  const lines = _.clone(content);

  const items = [];
  let idCounter = 1,
    lastDepth = 1,
    lastItemType;

  // Maps to track parent-child relationships
  const depthMap = {}; // Maps depth to last item at that depth

  const getAssessmentType = type => {
    const typeMappings = {
      LE: "le",
      "LEARNING ENGAGEMENT": "le",
      "FORMATIVE ASSESSMENT": "fmt",
      "SUMMATIVE ASSESSMENT": "smt",
      AI_TUTOR: "ai_tutor",
      WORKSHEET: "worksheet",
    };

    const defaultType = _.lowerCase(type);
    return _.get(typeMappings, type, defaultType);
  };

  _.forEach(lines, _line => {
    // Skip empty lines
    if (_.isEmpty(_.trim(_line))) return;

    let line = _line;

    // Calculate depth based on relative indentation
    const indentMatch = line.match(REGEX_PATTERNS.INDENTATION);
    const currentIndent = indentMatch ? indentMatch[0].length : 0;

    // Depth 1 for no spaces, otherwise 1 + number of spaces
    let depth = 1;
    if (currentIndent > 0) {
      depth = 1 + currentIndent;

      if (depth > lastDepth) {
        // depth can only be greater by 1 than last depth
        // and that is also only possible when last item is a folder
        if (lastItemType === "FOLDER") {
          depth = lastDepth + 1;
        } else {
          depth = lastDepth;
        }
      }
    }

    lastDepth = depth;

    // remove * from the line
    line = line.replace(/[*]/g, "");

    // remove [ID: <number>] from the line
    line = line.replace(/\[ID:\s*\d+\]/g, "");

    // Extract type from brackets
    const typeMatch = line.match(/\[([A-Z_\s]+)\]/i);
    const type = typeMatch ? _.toUpper(typeMatch[1]) : "FOLDER";

    lastItemType = type;
    const isLink = type === "LINK";

    // Extract label and content
    let label = "";
    let subtext = "";

    const contentAfterType = line.substring(line.indexOf("]") + 1).trim();
    const colonIndex = contentAfterType.lastIndexOf(":");

    if (colonIndex > -1) {
      // extract URL if it's a link
      if (isLink) {
        const urlMatch = contentAfterType.match(/https?:\/\/[^\s]+/);
        const url = urlMatch ? urlMatch[0] : "";

        // Remove the url and any trailing colon or dash (with optional spaces) from the label
        label = _.trim(
          contentAfterType
            .replace(url, "")
            .replace(/[:\-]+\s*$/, "")
            .trim()
        );
        subtext = url;
      } else {
        // otherwise everything before the colon is label and after is subtext
        label = _.trim(contentAfterType.substring(0, colonIndex));
        subtext = _.trim(contentAfterType.substring(colonIndex + 1));
      }
    } else {
      label = _.trim(contentAfterType);
    }

    // Create the item structure
    const id = _.toString(idCounter);
    idCounter++;

    const resourceType =
      type === "FOLDER"
        ? "FOLDER"
        : _.includes(["RESOURCE", "LINK"], type)
        ? "ENTITY_FLOW_RESOURCE"
        : "ASSESSMENT";

    // Build parent relationship
    let parent = null;
    if (depth > 1) {
      const parentDepth = depth - 1;
      if (_.has(depthMap, parentDepth)) {
        parent = { id: depthMap[parentDepth] };
      }
    }

    // Create item based on type
    const node = {
      id,
      label,
      depth,
      resourceType,
      parent,
      isRepeated: false,
      repeatedResources: [],
      childrenV2: [],
      childrenHierarchyInfo:
        resourceType === "FOLDER"
          ? {
              maxDepth: 0,
              totalCount: 0,
              totalCountWithFolder: 0,
            }
          : null,
    };

    // Add additional properties based on type
    if (resourceType === "ASSESSMENT") {
      if (!_.isEmpty(subtext)) {
        // remove anything between [[]] from subtext
        subtext = subtext.replace(/\[\[.*?\]\]/g, "").trim();
        node.subtext = subtext;
      }

      node.item = {
        id: `${id}-item`,
        type: "LEARNING_ENGAGEMENT",
        title: {
          id: `${id}-item-title`,
          value: label,
        },
        academicTerm: {
          id: `${id}-item-academicTerm`,
          lockingStatus: "UNLOCKED",
        },
        assessmentType: {
          id: `${id}-item-assessmentType`,
          value: getAssessmentType(type),
        },
        image: {
          id: `${id}-item-image`,
          value: null,
        },
        category: null,
      };
    } else if (resourceType === "ENTITY_FLOW_RESOURCE") {
      node.resourceType = "ORGANIZATION_RESOURCE";

      if (isLink) {
        const urlMatch = subtext.match(/https?:\/\/[^\s]+/);
        const url = urlMatch ? urlMatch[0] : "";
        const textContent = _.trim(subtext.replace(url, ""));

        node.item = {
          id: `${id}-item`,
          attachment: {
            id: `${id}-item-attachment`,
            type: "LINK",
            attachmentTitle: null,
            name: label,
            mimeType: null,
            url,
            signedUrl: url,
            thumbUrl: null,
          },
        };
        if (!_.isEmpty(textContent)) {
          node.subtext = textContent;
        }
      } else {
        node.item = {
          id: `${id}-item`,
          attachment: {
            id: `${id}-item-attachment`,
            type: "NOTE",
            title: `<b>${label}:</b> ${subtext}`,
            name: null,
            mimeType: null,
            url: null,
            signedUrl: null,
            thumbUrl: null,
          },
        };
      }
    }

    // Add to depthMap for parent-child tracking
    depthMap[depth] = id;

    // If this item has a parent, add it to parent's children
    if (parent) {
      const parentItem = _.find(items, { id: parent.id });

      if (parentItem) {
        parentItem.childrenV2.push({ id });
      }
    }

    items.push(node);
  });

  return items;
};

/**
 * Generates resource relation config for NestedAccordion
 * @param {Array} items - Array of accordion items
 * @returns {Object} Resource relation configuration
 */
export const generateResourceRelationConfig = items => {
  const parentConfig = {};
  const childrenConfig = {};

  _.forEach(items, node => {
    const itemId = node.id;
    parentConfig[itemId] = [];

    // Find all ancestors for this item
    let currentParent = node.parent;
    while (currentParent) {
      parentConfig[itemId].push(currentParent.id);
      const parentNode = _.find(items, { id: currentParent.id });
      currentParent = _.get(parentNode, "parent");
    }

    // For folders, collect all children
    if (node.resourceType === "FOLDER") {
      childrenConfig[itemId] = _.map(
        getNestedChildren({
          items,
          id: itemId,
        }),
        "id"
      );
    }
  });

  return {
    parentConfig,
    childrenConfig,
    resources: items,
  };
};

export const getBlocks = ({ content }) => {
  if (!content) return [];

  const lineSeparatedContent = _.split(content, "\n");
  const blocks = [];

  let lastBlockType, lastBlock, currentBlockType;
  let isInsideCodeBlock = false;
  let codeBuffer = "";

  _.forEach(lineSeparatedContent, line => {
    const trimmedLine = _.trim(line);

    // Check for triple backtick pattern to toggle code block state
    const isTripleBacktick = _.startsWith(trimmedLine, "```");

    if (isTripleBacktick) {
      if (isInsideCodeBlock) {
        // closing ```
        codeBuffer += line + "\n";

        // push whole code block as a single string
        const codeBlock = {
          type: "FieldView",
          content: [codeBuffer],
        };
        blocks.push(codeBlock);

        lastBlock = codeBlock;
        lastBlockType = "FieldView";
        isInsideCodeBlock = false;
        codeBuffer = "";
      } else {
        // opening ```
        isInsideCodeBlock = true;
        codeBuffer = line + "\n"; // includes ```
      }
      return;
    }

    // ---------- inside ```...``` ----------
    if (isInsideCodeBlock) {
      if (_.isEmpty(trimmedLine)) {
        if (lastBlock) {
          lastBlock.content.push(line);
        }
        return;
      }

      const hasHTMLTags = REGEX_PATTERNS.HTML_TAG_PATTERN.test(line);
      currentBlockType = hasHTMLTags ? "HTMLView" : "FieldView";

      codeBuffer += line + "\n";
      return;
    } else {
      // Normal block processing
      currentBlockType = getBlockTypeFromContent(line);
    }

    if (currentBlockType === lastBlockType) {
      lastBlock.content.push(line);
    } else {
      if (_.isEmpty(trimmedLine)) return;

      lastBlock = {
        type: currentBlockType,
        content: [line],
      };
      blocks.push(lastBlock);
      lastBlockType = currentBlockType;
    }
  });

  return blocks;
};

/**
 * Parses FieldView content and removes UID header
 * @param {Array<string>} content - Array of content lines
 * @returns {Object} Parsed content and field type
 */
export const parseFieldViewContent = content => {
  if (_.isEmpty(content)) return { parsedContent: content, type: "simple" };

  // Remove UID header
  const uidLineIndex = _.findIndex(
    content,
    line => _.includes(line, "**UID:") || _.includes(line, "** UID:")
  );

  const contentWithoutUID =
    uidLineIndex !== -1
      ? [...content.slice(0, uidLineIndex), ...content.slice(uidLineIndex + 1)]
      : [...content];

  // Determine if it's a list - simplify to just check for lines starting with -, *, or number.
  const listItems = _.filter(contentWithoutUID, line =>
    REGEX_PATTERNS.LIST_ITEM.test(line)
  );

  if (_.isEmpty(listItems)) {
    // Remove various number formats: large numbers, [number], (number), and standalone numbers
    const filteredContent = _.map(contentWithoutUID, line => {
      // Replace numbers larger than 6 digits with empty string
      let filteredLine = line.replace(REGEX_PATTERNS.LARGE_NUMBERS, "");
      // Replace [<number>] format with empty string
      filteredLine = filteredLine.replace(REGEX_PATTERNS.BRACKET_NUMBERS, "");
      // Replace (<number>) format with empty string
      filteredLine = filteredLine.replace(
        REGEX_PATTERNS.PARENTHESIS_NUMBERS,
        ""
      );
      return filteredLine;
    });

    return { parsedContent: filteredContent, type: "simple" };
  }

  // Check for nesting
  const indentations = _.map(listItems, item => {
    const match = item.match(REGEX_PATTERNS.INDENTATION);
    return match ? match[1].length : 0;
  });

  const hasMultipleIndentLevels = _.uniq(indentations).length > 1;

  return {
    parsedContent: contentWithoutUID,
    type: hasMultipleIndentLevels ? "nestedList" : "simpleList",
  };
};

/**
 * Parses list content into a format suitable for TextList component
 * @param {Array<string>} content - Array of content lines
 * @param {boolean} isNested - Whether the list is nested
 */
export const parseListForTextList = (content, isNested = false) => {
  const listItems = [];
  const itemsByIndent = {};
  const topLevelKeys = [];

  let title;

  // First pass: Create items and track their indentation levels
  _.forEach(content, (line, index) => {
    let matched = false;
    let indentLevel = 0;
    let key = `item-${index}`;
    let value = "";

    // Try the comprehensive flexible pattern first
    const flexMatch = line.match(REGEX_PATTERNS.FLEXIBLE_NUMBER_PATTERN);
    if (flexMatch) {
      matched = true;
      const [
        ,
        indentation,
        listMarker,
        textBefore,
        bracketNum,
        parenthesisNum,
        plainNum,
        textAfter,
      ] = flexMatch;
      indentLevel = indentation ? indentation.length : 0;

      // Get the ID from whichever number format was matched
      const id = bracketNum || parenthesisNum || plainNum || `item-${index}`;

      // Combine text before and after the number
      let fullText = textBefore || "";
      if (textAfter) {
        fullText = fullText.trim() + " " + textAfter;
      }

      key = id;
      value = _.trim(fullText);
    }
    // If the flexible pattern didn't match, try the legacy patterns
    else {
      // Try each pattern until one matches
      const listItemPatterns = [
        REGEX_PATTERNS.LIST_ITEM_WITH_BRACKET_NUMBER,
        REGEX_PATTERNS.LIST_ITEM_WITH_PARENTHESIS_NUMBER,
        REGEX_PATTERNS.LIST_ITEM_WITH_TEXT_NUMBER,
      ];

      for (const pattern of listItemPatterns) {
        const match = line.match(pattern);
        if (match) {
          matched = true;
          const [, indentation, listMarker] = match;
          indentLevel = indentation ? indentation.length : 0;

          // Extract the ID and text differently based on pattern
          if (pattern === REGEX_PATTERNS.LIST_ITEM_WITH_BRACKET_NUMBER) {
            // Pattern 1: [number] format
            const [, , , id, text] = match;
            key = id || `item-${index}`;
            value = _.trim(text);
          } else if (
            pattern === REGEX_PATTERNS.LIST_ITEM_WITH_PARENTHESIS_NUMBER
          ) {
            // Pattern 2: (number) format
            const [, , , textBefore, id, textAfter] = match;
            key = id || `item-${index}`;
            value = _.trim(`${textBefore || ""} ${textAfter || ""}`);
          } else if (pattern === REGEX_PATTERNS.LIST_ITEM_WITH_TEXT_NUMBER) {
            // Pattern 3: number in text
            const [, , , textBefore, id, textAfter] = match;
            key = id || `item-${index}`;
            value = _.trim(`${textBefore || ""} ${textAfter || ""}`);
          }
          break;
        }
      }
    }

    // If none of the patterns matched, use a simple check for list items
    if (!matched && line.match(REGEX_PATTERNS.LIST_ITEM)) {
      const indentation = line.match(REGEX_PATTERNS.INDENTATION)[1];
      indentLevel = indentation ? indentation.length : 0;
      value = line.replace(REGEX_PATTERNS.LIST_ITEM, "").trim();
    }

    // Skip if not a list item
    if (!matched && !line.match(REGEX_PATTERNS.LIST_ITEM)) {
      // if it starts with ** it may be a title
      if (line.startsWith("**")) {
        title = line;
      }
      return;
    }

    // Create the list item
    const item = { key, value };

    // Track items by their indentation level
    if (!itemsByIndent[indentLevel]) {
      itemsByIndent[indentLevel] = [];
    }
    itemsByIndent[indentLevel].push(key);

    // Track top level items
    if (indentLevel === 0) {
      topLevelKeys.push(key);
    }

    listItems.push(item);
  });

  // Create root item that contains all top level items
  const rootItem = {
    key: "root",
    value: "",
    children: topLevelKeys,
  };

  // If this is a nested list, we need to establish parent-child relationships
  if (isNested && !_.isEmpty(itemsByIndent)) {
    // Sort indentation levels (0, 2, 4, etc.)
    const indentLevels = _.sortBy(Object.keys(itemsByIndent).map(Number));

    // For each indentation level (except the smallest/top level)
    for (let i = 1; i < indentLevels.length; i++) {
      const currentLevel = indentLevels[i];
      const parentLevel = _.findLast(
        indentLevels,
        level => level < currentLevel
      );

      // If we found a valid parent level
      if (!_.isUndefined(parentLevel)) {
        // For each item at the current indentation level
        _.forEach(itemsByIndent[currentLevel], currentKey => {
          // Find the closest preceding item from the parent level
          const currentItemIndex = _.findIndex(listItems, { key: currentKey });

          // Look backwards through the list to find the nearest parent
          let parentKey = null;
          for (let j = currentItemIndex - 1; j >= 0; j--) {
            const potentialParent = listItems[j];
            if (_.includes(itemsByIndent[parentLevel], potentialParent.key)) {
              parentKey = potentialParent.key;
              break;
            }
          }

          // If we found a parent, add this item as its child
          if (parentKey) {
            const parentItem = _.find(listItems, { key: parentKey });
            if (parentItem) {
              if (!parentItem.children) {
                parentItem.children = [];
              }
              parentItem.children.push(currentKey);
            }
          }
        });
      }
    }
  }

  // Add the root item and then all other items
  return { listItems: [rootItem, ...listItems], title };
};

export const getContentInsideTripleBackticks = content => {
  if (_.isEmpty(content)) return null;

  const contentInTripleBackticks = _.first(content.match(/```([\s\S]*?)```/));
  return contentInTripleBackticks;
};

// Helper function to extract concept data from content

const getIsNumber = value => {
  return /^\d+$/.test(value);
};

const getIsArrayOfStrings = value => _.every(value, item => _.isString(item));

export const getUpdatedValue = ({
  value,
  fieldObj,
  fieldTemplateObj,
  isPorted,
  globalContextValue,
  globalContextExplorationMap,
}) => {
  const fieldType = isPorted
    ? _.get(fieldTemplateObj, "uiType.value")
    : fieldTemplateObj?.type;

  switch (fieldType) {
    case "InputNumberField": {
      // fields such as hours, duration, etc only accept numbers
      if (_.isString(value) && getIsNumber(value)) {
        return parseInt(value, 10);
      }
      return value;
    }

    case "IBMultiSelectQuestion": {
      return _.reduce(
        value,
        (result, item) => {
          const { parentId: id, description: text } = item ?? {};
          if (!id || !text) {
            return result;
          }

          if (_.has(result, id)) {
            // If the id already exists, append the text to q1
            result[id].q1 += `\n${text}`;
            return result;
          }

          result[id] = {
            q1: text,
          };

          return result;
        },
        {}
      );
    }

    case "UIInputCheckList":
    case "InputNumberList": {
      const hasSelection =
        fieldType === "UIInputCheckList" ||
        _.get(fieldTemplateObj, "config.fieldTypeConfig.hasSelection", false);

      let updatedValue;

      if (hasSelection) {
        const isAlreadyInCorrectFormat = _.every(value, item => {
          const itemId = item?.id;
          const hasSelectionBool = _.has(item, "isSelected");
          const hasValue = _.has(item, "value") && !_.isEmpty(item.value);

          return !_.isEmpty(itemId) && hasSelectionBool && hasValue;
        });

        if (isAlreadyInCorrectFormat) {
          return value;
        }

        const isArrayOfStrings = getIsArrayOfStrings(value);

        // for values in format like: ["Who we are", "How we organize.."]
        // we will find the id of it in the available options,
        // 1. if id is present, we will use that id
        // 2. if id is not present, we will generate a random id
        // we also need to include all the pre-existing options from unit plan
        if (isArrayOfStrings) {
          let currentOptions = _.cloneDeep(_.get(fieldObj, "value", []));
          const isOptionsEmpty =
            _.isEmpty(currentOptions) ||
            _.every(currentOptions, item => {
              return _.isEmpty(item?.value) && _.isEmpty(item?.label);
            });

          if (isOptionsEmpty) {
            // if current options are empty, we will use the global context value
            currentOptions = _.get(
              globalContextExplorationMap,
              [globalContextValue, "explorations"],
              []
            );
          }

          const preExistingValues = [];

          // include all the existing options first
          updatedValue = _.reduce(
            currentOptions,
            (result, item) => {
              const label = item.value ?? item.label;
              const isSelected = _.includes(value, label);

              if (isSelected) {
                preExistingValues.push(label);
              }

              result.push({
                id: item.id,
                value: label,
                isSelected,
              });

              return result;
            },
            []
          );

          // then add the new values
          const newValues = _.difference(value, preExistingValues);

          _.forEach(newValues, item => {
            updatedValue.push({
              id: generateRandomId(),
              value: item,
              isSelected: true,
            });
          });

          return updatedValue;
        }

        // if it is an array of objects, make sure each of them has an id
        const isInInvalidFormat =
          _.isObject(value) &&
          _.some(value, item => {
            const itemId = item?.id;

            if (_.isEmpty(itemId)) {
              return true;
            }

            return false;
          });

        if (isInInvalidFormat) {
          return {};
        }

        const selectedValues = _.filter(value, item => {
          const isNumber = getIsNumber(item);
          return isNumber;
        });

        updatedValue = _.cloneDeep(_.get(fieldObj, "value", []));
        updatedValue = _.map(updatedValue, item => {
          const isSelected = _.includes(selectedValues, item.id);

          return {
            ...item,
            isSelected,
          };
        });

        const newValues = _.reduce(
          value,
          (result, item) => {
            const isNumber = getIsNumber(item);
            if (!isNumber) {
              result.push({
                id: generateRandomId(),
                value: item,
                isSelected: true,
              });
            }

            return result;
          },
          []
        );

        updatedValue = [...updatedValue, ...newValues];
      } else {
        updatedValue = _.map(value, item => {
          if (_.isString(item)) {
            // if its a number, we will later find the text
            // from unit plan value
            const isNumber = getIsNumber(item);
            if (isNumber) {
              return {
                id: item,
              };
            }

            // if its not a number, we need to replicate
            // (+) add another functionality
            return {
              id: generateRandomId(),
              value: item,
            };
          }

          return item;
        });
      }

      return updatedValue;
    }

    case "GenericCardList": {
      if (_.isArray(value)) {
        return _.map(value, item => {
          if (!_.has(item, "label") && _.has(item, "value")) {
            return { ...item, label: item.value };
          }

          return item;
        });
      }

      return value;
    }

    case "RichText": {
      let newValue = value;
      if (_.isArray(value)) {
        const isArrayOfStrings = getIsArrayOfStrings(value);

        if (isArrayOfStrings) {
          newValue = _.join(value, "<br />");
        } else {
          // prevent adding invalid values
          return "";
        }
      }

      if (_.isObject(value) && _.isEmpty(newValue)) {
        return "";
      }

      newValue = convertMarkdownToHTML({ content: newValue });
      newValue = replaceHTMLHeadings(newValue);

      return newValue;
    }

    default:
      return value;
  }
};

/**
 * @deprecated Not being used anymore, to be removed in future
 */

const pypElementSetMapping = {
  lp: "learnerProfiles",
  action: "actions",
  concepts: "concepts",
  theme: "themes",
  atls: "atls",
  benchmarks: "benchmarks",
  subjects: "subjects",
  relatedConcepts: "relatedConcepts",
  subjectGroup: "subjectGroups",
  learningStandardUBD: "learningStandardUBD",
  atlsUBD: "atlsUBD",
  atlsMYP: "atlsMYP",
  learningStandardMYP: "learningStandardMYP",
};

const getResolvedMinimalTreeSet = ({ fields, uid, curriculumType }) => {
  const fieldData = _.find(fields, { uid });

  if (uid === "subjects") {
    return _.get(fieldData, `resolvedMinimalTree.subjects`, []);
  }

  if (uid === "unitPlan") {
    return _.get(fieldData, `resolvedMinimalTree.unitPlan`, {});
  }

  if (uid === "pypNestedElement") {
    return _.get(fieldData, `resolvedMinimalTree.nodes`, []);
  }

  switch (curriculumType) {
    case CURRICULUM_TYPE_PYP:
      return _.get(
        fieldData,
        `resolvedMinimalTree.${pypElementSetMapping[uid]}`,
        []
      );
    default:
      return _.get(fieldData, `resolvedMinimalTree.nodes`, []);
  }
};

const getMYPObjectiveLevelFromSubject = params => {
  const { objectiveLevelNodes, subjects, showSubject = false } = params;

  if (
    ACLStore.can("FeatureFlag:RemoveMYPObjectivesPhase") ||
    _.isEmpty(objectiveLevelNodes)
  ) {
    return "";
  }

  const objectiveLevels = _.reduce(
    subjects,
    (list, subject) => {
      const nodes = _.filter(objectiveLevelNodes, objectiveNode => {
        return _.find(
          _.get(objectiveNode, "dataSetNodeWrapper.properties.subjects", []),
          subj => {
            return _.isEqual(subj?.id, subject?.id);
          }
        );
      });

      const objectiveLabels = _.join(_.map(nodes, "label"), " | ");

      if (!_.isEmpty(nodes)) {
        if (showSubject) {
          list.push(_.join([objectiveLabels, subject?.name], " - "));
        } else {
          list.push(objectiveLabels);
        }
      }

      return list;
    },
    []
  );

  return objectiveLevels;
};

const SHOW_COUNT_FIELDS = [
  "atls",
  "relatedConcepts",
  "benchmarks",
  "learningStandardUBD",
  "atlsUBD",
  "atlsMYP",
  "learningStandardMYP",
  "relatedConceptsMYP",
  "inquiryQuestions",
  "objectivesMYP",
  "interdisciplinaryCriteriaMYP",
  "atlDP",
  "subjectStandardDP",
  "syllabusContentDP",
];

const INTERDISCIPLINARY_FIELD_MAPPING = {
  learningStandardMYP: "disciplinaryLearningStandardMYP",
  objectivesMYP: "disciplinaryObjectivesMYP",
  relatedConceptsMYP: "disciplinaryRelatedConceptsMYP",
};

export const getContextFieldValue = params => {
  const {
    t,
    field,
    type,
    course,
    config,
    context,
    unitPlanDetails,
    learningCourse,
    unitPlanTemplate,
    curriculumProgramType,
  } = params;

  // handled separately, just for course grade
  if (field === "grades" && type === "COURSE") {
    const value =
      _.get(course?.primaryGrade, "name") ||
      _.get(learningCourse?.primaryGrade, "name") ||
      _.join(_.map(course?.grades, "name"), ", ") ||
      _.join(_.map(unitPlanDetails?.grades, "name"), ", ");

    return {
      value,
    };
  } else if (field === "objectiveLevel" && type === "UNIT") {
    const subjects = getResolvedMinimalTreeSet({
      fields: unitPlanDetails?.allFields,
      uid: "subjects",
      curriculumType: curriculumProgramType,
    });

    const objectiveLevelNodes = getResolvedMinimalTreeSet({
      fields: unitPlanDetails?.allFields,
      uid: "objectiveLevel",
      curriculumType: curriculumProgramType,
    });

    const objectiveLevels = getMYPObjectiveLevelFromSubject({
      objectiveLevelNodes,
      subjects,
      showSubject: true,
    });

    if (!_.isEmpty(objectiveLevels)) {
      return { value: _.join(objectiveLevels, ", ") };
    }

    return { value: null };
  }

  const unitPlanFields = _.get(unitPlanDetails, "allFields", []);
  const unitPlanFieldList = _.get(unitPlanTemplate, "field_list", {});
  const unitPlanFieldData = _.find(unitPlanFields, { uid: field });
  const unitPlanFieldValue = _.get(unitPlanFieldData, "value");

  const subjectIds = _.get(
    _.find(unitPlanFields, { uid: "subjects" }),
    "value"
  );

  let valueKeys = unitPlanFieldValue ?? context?.[field];

  const fieldObj = unitPlanFieldList[field];
  const isMappedWithDetailsFields = _.get(
    fieldObj,
    "config.isMappedWithDetailsFields",
    false
  );

  if (isMappedWithDetailsFields) {
    const mappedDetailsFields = _.get(
      fieldObj,
      "config.mappedDetailsFields",
      []
    );

    valueKeys = getUnitPlanDetailsValueFromFieldKey({
      unitPlanDetails: unitPlanDetails,
      mappedDetailsFields,
      key: field,
    });
  }

  const fieldType =
    _.get(fieldObj, "config.type") || _.get(fieldObj, "config.viewType");

  const resolvedValue = _.get(unitPlanFieldData, "resolvedMinimalTree");

  let value;

  const isInterdisciplinaryUnit = getIsUnitInterdisciplinary({
    unitPlanDetails,
  });
  const isDynamicField = _.has(INTERDISCIPLINARY_FIELD_MAPPING, field);

  // for interdisciplinary fields, accumulate values from all grounding fields
  if (isInterdisciplinaryUnit && isDynamicField) {
    const interdisciplinaryField = INTERDISCIPLINARY_FIELD_MAPPING[field];

    value = _.reduce(
      unitPlanFields,
      (result, item) => {
        const disciplinaryUid = item?.uid;
        if (
          _.startsWith(disciplinaryUid, interdisciplinaryField) &&
          // should not be an exact match, that field is a placeholder for config
          disciplinaryUid !== interdisciplinaryField
        ) {
          const interdisciplinaryValueKeys = _.get(item, "value", []);

          if (!_.isEmpty(interdisciplinaryValueKeys)) {
            return [...result, ...interdisciplinaryValueKeys];
          }
        }

        return result;
      },
      []
    );

    if (!_.isEmpty(value)) {
      const selectedCount = t("common:lowercase", {
        text: t("common:label_seleceted", {
          count: _.size(value),
        }),
      });

      return {
        value: selectedCount,
      };
    }
  } else {
    value = getElementValue({
      t,
      fieldUID: field,
      valueKeys,
      unitPlanFieldValue,
      fieldObj: fieldObj || config,
      subjectIds: subjectIds,
      resolvedValue: resolvedValue || {},
      isUnitPlan: type === "UNIT",
      unitPlanFields,
      subjects: _.get(
        _.find(unitPlanFields, { uid: "subjects" }),
        "resolvedMinimalTree.subjects",
        []
      ),
      shouldReturnDurationInWeeks: true,
      skipRemoveSubjectCheck: true,
    });
  }

  if (_.isArray(value) && !_.isEmpty(value)) {
    const selectedCount = t("common:lowercase", {
      text: t("common:label_seleceted", {
        count: _.size(valueKeys),
      }),
    });

    if (_.includes(SHOW_COUNT_FIELDS, field)) {
      // TODO: hook actual tooltip later
      // const tooltip = buildLearningGoalsHierarchy(
      //   getFlattenedNodes({
      //     nodes: value,
      //   })
      // );
      return { value: selectedCount };
    } else if (_.every(value, _.isString)) {
      return { value: _.join(value, ", ") };
    } else if (_.every(value, item => _.has(item, "value"))) {
      return { value: _.join(_.map(value, "value"), ", ") };
    } else {
      return { value: selectedCount };
    }
  } else {
    if (field === "inquiryQuestions") {
      const _value = _.flatten(_.values(value));
      if (!_.isEmpty(_value)) {
        return {
          value: t("common:lowercase", {
            text: t("common:label_seleceted", {
              count: _.size(_value),
            }),
          }),
        };
      } else return { value: null };
    } else if (fieldType === "RichText") {
      return { value: _.trim(extractContentFromHTML({ htmlString: value })) };
    } else {
      return { value: _.trim(htmlToText(value)) };
    }
  }
};

export const getDefaultContextFields = params => {
  const {
    curriculumProgramType,
    isInterdisciplinaryUnit,
    isLearningCourseEnabled = false,
  } = params;

  const isCourseBasedGrading = getIsCourseBasedGradingEnabled();
  const isSubjectRequired = ACLStore.can(SUBJECT_REQUIREMENT_PERM);

  const DEFAULT_CONTEXT_FIELDS = {
    [CURRICULUM_TYPE_PYP]: [
      {
        field: "subjects",
        type: "UNIT",
        isRequired: isSubjectRequired,
        shouldHide: isCourseBasedGrading,
      },
      {
        field: "theme",
        type: "UNIT",
        isRequired: false,
        shouldHide: !isInterdisciplinaryUnit,
      },
      {
        field: "atls",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "benchmarks",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "concepts",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "relatedConcepts",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "lp",
        type: "UNIT",
        isRequired: false,
      },
    ],
    [CURRICULUM_TYPE_MYP]: [
      {
        field: "subjects",
        type: "UNIT",
        config: {
          label: "common:subject_plural",
          isLocalised: true,
        },
        isRequired: isSubjectRequired,
        // There will always be a subject in MYP for subject specific units
        // and it can't be changed, not even from middle pane
        isDisabled: isLearningCourseEnabled || !isInterdisciplinaryUnit,
        shouldHide: isCourseBasedGrading,
      },
      {
        field: "objectiveLevel",
        type: "UNIT",
        config: {
          label: "unitPlan:myp_objective_year_phase",
          isLocalised: true,
        },
        isRequired: false,
        isDisabled: true,
      },
      {
        field: "atlsMYP",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "globalContextMYP",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "conceptsMYP",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "learningStandardMYP",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "relatedConceptsMYP",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "objectivesMYP",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "interdisciplinaryCriteriaMYP",
        type: "UNIT",
        isRequired: false,
        shouldHide: !isInterdisciplinaryUnit,
      },
    ],
    [CURRICULUM_TYPE_UBD]: [
      {
        field: "subjects",
        type: "UNIT",
        isRequired: isSubjectRequired,
        shouldHide: isCourseBasedGrading,
      },
      {
        field: "learningStandardUBD",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "atlsUBD",
        type: "UNIT",
        isRequired: false,
      },
    ],
    [CURRICULUM_TYPE_DP]: [
      {
        field: "subjects",
        type: "UNIT",
        isRequired: isSubjectRequired,
        isDisabled: true,
        shouldHide: isCourseBasedGrading,
      },
      {
        field: "subjectLevel",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "syllabusContentDP",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "atlDP",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "learnerProfileDP",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "subjectStandardDP",
        type: "UNIT",
        isRequired: false,
      },
    ],
  };

  const defaultContextFields = DEFAULT_CONTEXT_FIELDS[curriculumProgramType];

  return _.filter(
    defaultContextFields,
    ({ shouldHide = false }) => !shouldHide
  );
};

/**
 * Parses list view content (array of lines) into a flat TextListData array.
 * Each line is processed to remove list markers and extra spaces, converted to HTML using convertMarkdownToHTML,
 * and then assembled into a tree where nesting is determined by the number of leading spaces (2 spaces = one level).
 * Also determines whether the list is ordered or unordered based on its marker.
 *
 * @param {Array<string>} rawContent - Array of list view lines.
 * @returns {Object} Object containing listData (Array) and ordered (boolean)
 */
export const parseListViewContentToTextListData = rawContent => {
  if (!rawContent || rawContent.length === 0)
    return { listData: [], ordered: false };

  // Determine if list is ordered by checking the first non-empty line marker
  let isOrdered = false;
  const firstLineMatch = rawContent[0].match(/^\s*([-\d]+\.?)\s+/);
  if (firstLineMatch) {
    const marker = firstLineMatch[1];
    isOrdered = /^\d+\.?$/.test(marker);
  }

  // Filter out empty lines
  const lines = rawContent.filter(line => line.trim().length > 0);

  const result = [];
  // Root node
  const root = { key: "root", children: [] };
  result.push(root);

  // Stack holds { node, depth, childCount }
  const stack = [{ node: root, depth: -1, childCount: 0 }];

  // For each line, determine depth, clean marker and convert content
  lines.forEach(rawLine => {
    // Determine number of leading spaces
    const leadingSpacesMatch = rawLine.match(/^(\s+)/);
    const spaces = leadingSpacesMatch ? leadingSpacesMatch[1].length : 0;
    // Depth: every 2 spaces increment depth by 1
    const depth = Math.floor(spaces / 2);

    // Remove leading spaces and list marker (-, digit, or digit followed by '.')
    const cleanedLine = rawLine.replace(/^\s*([-\d]+\.?)\s+/, "").trim();
    // Convert any markdown styling to HTML
    const htmlValue = convertMarkdownToHTML({
      content: cleanedLine,
      shouldParseContent: true,
    });

    // Pop stack until top has depth < current depth
    while (stack.length && stack[stack.length - 1].depth >= depth) {
      stack.pop();
    }
    const parentItem = stack[stack.length - 1];
    // Increase parent's childCount and build key using parent's key and letter sequence (a, b, c, ...)
    parentItem.childCount++;
    const letter = String.fromCharCode(96 + parentItem.childCount); // 1 -> a, 2 -> b, etc.
    const newKey =
      parentItem.node.key === "root"
        ? `${parentItem.childCount}-${letter}`
        : `${parentItem.node.key}-${letter}`;

    const newNode = { key: newKey, value: htmlValue, children: [] };
    // Append this node's key to parent's children array
    parentItem.node.children.push(newKey);
    // Add newNode to result
    result.push(newNode);
    // Push new node onto the stack for potential children (childCount starts at 0)
    stack.push({ node: newNode, depth, childCount: 0 });
  });

  const rootItemChildrenCount = _.size(_.get(root, "children", []));

  return {
    listData: result,
    // no point in having an ordered list with only 1 item
    isOrdered: rootItemChildrenCount === 1 ? false : isOrdered,
  };
};

const getUnitTypeForPromptFilters = unitType => {
  switch (unitType) {
    case "ibStandAlone":
      return "ibPypSubjectSpecific";
    case "ibInquiry":
      return "ibPypTransdisciplinary";
    case "ibMYPSubjectSpecific":
      return "mypSubjectSpecific";
    case "ubdInquiry":
      return "ubdInterdisciplinary";
    case "ibDPSubjectSpecific":
      return "dpSubjectSpecific";
    default:
      return unitType;
  }
};

export const getPromptFieldsFromMetadata = metadata => {
  const { preRequisites } = metadata || {};
  if (!_.isArray(preRequisites) || _.isEmpty(preRequisites)) return [];

  return _.compact(
    _.map(preRequisites, item => {
      const match = item.match(/{{\s*([^}]+)\s*}}/);
      if (match) {
        const templateContent = _.trim(match[1]);
        if (_.includes(templateContent, ".")) {
          const parts = _.split(templateContent, ".");
          return { type: _.trim(parts[0]), fieldUid: _.trim(parts[1]) };
        }
        return { type: null, fieldUid: templateContent };
      }
      return null;
    })
  );
};

const FIELDS_TO_IGNORE_FOR_UNIT_PLAN_FILLING = [
  // prefilled always:
  "unitType",
  "dateDuration",
  "duration",
  "startDate",
  "endDate",
  "hours",
  "subjectGroup",
  "subjects",
  "objectiveLevel",
  "title",
  // dependent fields:
  "conceptsQuestion",
  "actionQuestion",
  "studentAction",
];

const checkIsCompleteForPortedField = ({ field, fieldTemplateObj }) => {
  const fieldType = _.get(fieldTemplateObj, "uiType.value");
  const value = field.value;

  switch (fieldType) {
    case "InputNumberList":
      return (
        !_.isEmpty(value) &&
        _.some(value, item => {
          if (_.has(item, "value")) {
            return !_.isEmpty(item.value);
          }
          return !_.isEmpty(item);
        })
      );

    default:
      return !_.isEmpty(value);
  }
};

const getIsUnitPlanFilled = ({ unitPlanDetails, fieldList, fieldListV2 }) => {
  const unitPlanFields = _.get(unitPlanDetails, "allFields", []);
  if (_.isEmpty(unitPlanFields)) return false;

  // Check if all required fields are filled
  let filledFields = _.filter(unitPlanFields, field => {
    const { uid: fieldKey } = field;

    if (_.includes(FIELDS_TO_IGNORE_FOR_UNIT_PLAN_FILLING, fieldKey)) {
      return false; // Ignore these fields
    }

    const fieldObj = _.get(fieldList, fieldKey, {});
    const isComplete = checkEmptyElementValue({
      fieldKey,
      data: unitPlanDetails,
      unitPlanFields,
      fieldObj,
      isUnitPlan: true,
      getElementValue,
    });

    return isComplete;
  });

  // check if some custom fields are filled
  filledFields = _.filter(filledFields, field => {
    const isPorted = _.has(fieldListV2, field.uid);
    if (!isPorted) return true;

    const fieldTemplateObj = _.get(fieldListV2, field.uid, {});
    return checkIsCompleteForPortedField({
      field,
      fieldTemplateObj,
    });
  });

  return _.size(filledFields) > 0;
};

export const getAIPromptsQueryVariables = (params = {}) => {
  const {
    unitPlanId,
    curriculumType,
    curriculumProgramId,
    gradeId,
    isUnitFlow,
    unitType,
    unitPlanDetails,
    fieldList,
    fieldListV2,
  } = params;

  const isUnitPlanFilled =
    getIsUnitPlanFilled({ unitPlanDetails, fieldList, fieldListV2 }) &&
    !isUnitFlow;

  const categories = [
    {
      entityType: "CURRICULUM_PROGRAM",
      entities: [curriculumType],
    },
    {
      entityType: "UNIT_TYPE",
      entities: [getUnitTypeForPromptFilters(unitType)],
    },
  ];

  let type;

  if (isUnitFlow) {
    type = "NEW_UNIT_FLOW";
  } else {
    type = isUnitPlanFilled ? "EXISTING_UNIT" : "NEW_UNIT";
  }

  return {
    filters: {
      entityId: unitPlanId,
      entityType: ENTITY_TYPE,
      categories,
      curriculumProgramId,
      gradeId,
      types: [type],
    },
  };
};

const getContextSwitcherOptions = ({ t }) => {
  return [
    {
      key: "UNIT_PLAN",
      value: "UNIT_PLAN",
      label: t("common:unit_plan"),
    },
    {
      key: "UNIT_FLOW",
      value: "UNIT_FLOW",
      label: t("unitPlan:unit_flow"),
    },
  ];
};

export const getContextSwitcherOptionsMemoized = getMemoizedLangFunction(
  getContextSwitcherOptions
);

/**
 * Condenses multiple continuous tool messages into a single message
 *
 * Input messages are in this format:
 * author: "assistant", content: "", toolCalls: [toolCallIds]
 * author: "tool", content JSON
 * author: "assistant", content: ...
 * author: "tool", content JSON
 * ...
 *
 * The output will combine all tool continuous tool messages into a single message
 *
 * @param {Object} param0 - Object containing messages
 * @param {Array} param0.messages - Array of message objects
 * @returns {Array} - Array of messages with continuous tool messages collapsed
 */
export const getCollapsedMessages = ({ messages }) => {
  if (!messages || !_.isArray(messages) || messages.length === 0) {
    return [];
  }

  const collapsed = [];
  let toolGroup = [];

  _.forEach(messages, edge => {
    const { node = {} } = edge;
    // Remove assistant messages with empty content.
    if (!node || !node?.authorType) return;
    if (node.authorType === "assistant" && _.isEmpty(node?.content)) {
      return;
    }
    // Group tool messages.
    if (node.authorType === "tool") {
      toolGroup.push(edge);
    } else {
      if (!_.isEmpty(toolGroup)) {
        if (toolGroup.length > 1) {
          const firstToolNode = _.cloneDeep(toolGroup[0].node);
          firstToolNode.toolResponses = _.map(
            toolGroup,
            item => item.node.content
          );
          firstToolNode.isCollapsed = true;
          collapsed.push({ node: firstToolNode });
        } else {
          collapsed.push(toolGroup[0]);
        }
        toolGroup = [];
      }
      collapsed.push(edge);
    }
  });

  if (!_.isEmpty(toolGroup)) {
    if (toolGroup.length > 1) {
      const firstToolNode = _.cloneDeep(toolGroup[0].node);
      firstToolNode.toolResponses = _.map(toolGroup, item => item.node.content);
      firstToolNode.isCollapsed = true;
      collapsed.push({ node: firstToolNode });
    } else {
      collapsed.push(toolGroup[0]);
    }
  }

  // swap assistant and tool message order if assistant message comes before tool message. Clone to avoid mutating original messages.
  // This will ensure to render "Content added to your unit:" above the assistant message.
  for (let i = 0; i < collapsed.length - 1; ) {
    const currentNode = collapsed[i].node;
    const nextNode = collapsed[i + 1].node;

    if (
      currentNode.authorType === "assistant" &&
      nextNode.authorType === "tool"
    ) {
      // Swap the messages
      const temp = _.cloneDeep(collapsed[i]);
      collapsed[i] = _.cloneDeep(collapsed[i + 1]);
      collapsed[i + 1] = temp;
      // Increment i to skip the next message since it's already processed
      i += 1;
    }
    i += 1;
    // Incremented inside the loop to avoid sonarjs/update-loop-counter rule warning
  }
  return collapsed;
};

export const getHierarchyOfField = ({ fieldUID, templateBody }) => {
  if (!fieldUID || !templateBody) return {};
  const hierarchy = {};
  const {
    step_list: stepList = [],
    stage_list: stageList = [],
    module_list: moduleList = [],
  } = templateBody;

  const stepUid = _.findKey(stepList, ({ fields = [] }) =>
    _.includes(fields, fieldUID)
  );

  if (!stepUid) return hierarchy;
  hierarchy.step = stepUid;

  const stageUid = _.findKey(stageList, ({ steps = [] }) =>
    _.includes(steps, stepUid)
  );
  if (!stageUid) return hierarchy;
  hierarchy.stage = stageUid;

  const moduleUid = _.findKey(moduleList, ({ stages = [] }) =>
    _.includes(stages, stageUid)
  );
  if (!moduleUid) return hierarchy;
  hierarchy.module = moduleUid;

  return hierarchy;
};

export const getConversationIds = ({ aiConversation }) => {
  const edges = _.get(aiConversation, "edges", []);

  const entityIdMap = _.reduce(
    edges,
    (result, item) => {
      const node = _.get(item, "node", {});
      const { id, entitySubType } = node;

      if (id && entitySubType) {
        result[entitySubType] = id;
      }

      return result;
    },
    {}
  );

  return {
    unitPlanConversationId: entityIdMap["UNIT_PLAN"],
    unitFlowConversationId: entityIdMap["UNIT_FLOW"],
  };
};

export const getHasUserFacingContent = ({ output, toolCalls }) => {
  const hasMutationToolCall = _.some(toolCalls, name =>
    _.includes(
      ["set_template_fields", "commit_final_unit_flow_to_unit_plan"],
      name
    )
  );

  if (hasMutationToolCall) return true;

  const { output: cleanedOutput } = extractStateAndContent(output);
  const outputWithoutNewLines = _.replace(cleanedOutput, /(\r\n|\n|\r)/g, "");

  if (_.isEmpty(_.trim(outputWithoutNewLines))) return false;

  return true;
};

export const getIsUnitInterdisciplinary = ({ unitPlanDetails }) => {
  const unitType = _.get(unitPlanDetails, "unitType.value", null);
  const isInterdisciplinaryUnit = _.includes(
    [MYP_INTERDISCIPLINARY, PYP_TRANSDISCIPLINARY, UBD_INQUIRY],
    unitType
  );

  return isInterdisciplinaryUnit;
};

const getIsMultiSelectFromPlannerElementSet = ({
  plannerElementSetOfFields,
  fieldObj,
}) => {
  const plannerElementSetId = _.get(
    fieldObj,
    "plannerElementSetId.value",
    null
  );

  // default to true
  if (!plannerElementSetId) return true;

  const isMultiSelect = _.chain(plannerElementSetOfFields)
    .find({ id: plannerElementSetId })
    .get("config")
    .find({ config: "selectionType" })
    .get("value")
    .isEqual("MULTI_SELECT")
    .value();

  return isMultiSelect;
};

const getFieldTypeFromTemplateType = ({
  fieldUID,
  fieldObj,
  isInterdisciplinaryUnit,
  isFieldListV2 = false,
  plannerElementSetOfFields,
}) => {
  const fieldType = isFieldListV2
    ? _.get(fieldObj, "uiType.value")
    : _.get(fieldObj, "type", "");

  // fieldUid is used for specific cases like subjects
  switch (fieldUID) {
    case "subjects":
      return isInterdisciplinaryUnit
        ? "MULTI_SELECT_CHECKLIST"
        : "SINGLE_SELECT_CHECKLIST";

    default:
      break;
  }

  switch (fieldType) {
    case "RichText":
      return "RICH_TEXT";

    case "NESTED_CHECKLIST":
    case "PlannerElementDataSet":
      return "NESTED_MULTI_SELECT_CHECKLIST";

    case "DateDurationSelectorV2":
      return "DATE_DURATION";

    case "InputNumberList":
      return "STRING_ARRAY";

    case "GenericCheckList":
      return "MULTI_SELECT_CHECKLIST";

    case "IBMultiSelectQuestion":
      return "STRING_ARRAY";

    case "GenericCardList": {
      const isMultiSelect = _.get(
        fieldObj,
        "config.fieldTypeConfig.allowMultiSelect",
        false
      );
      return isMultiSelect
        ? "MULTI_SELECT_CHECKLIST"
        : "SINGLE_SELECT_CHECKLIST";
    }

    case "CHECKLIST":
    case "CARD": {
      const isMultiSelect = getIsMultiSelectFromPlannerElementSet({
        plannerElementSetOfFields,
        fieldObj,
      });

      return isMultiSelect
        ? "MULTI_SELECT_CHECKLIST"
        : "SINGLE_SELECT_CHECKLIST";
    }

    case "InputField":
      return "SINGLE_LINE_TEXT";

    case "DurationFieldInput":
      return "NUMBER";

    default:
      return fieldType;
  }
};

export const generateFieldTypeMapping = ({
  fieldList,
  fieldListV2,
  isInterdisciplinaryUnit,
  plannerElementSetOfFields,
}) => {
  const _fieldTypeMapping = {};

  _.forEach(
    fieldList,
    (fieldObj, fieldUID) => {
      _fieldTypeMapping[fieldUID] = getFieldTypeFromTemplateType({
        fieldUID,
        fieldObj,
        isInterdisciplinaryUnit,
      });
    },
    {}
  );

  _.forEach(
    fieldListV2,
    (fieldObj, fieldUID) => {
      if (_.has(_fieldTypeMapping, fieldUID)) {
        // If the fieldUID already exists in the mapping, skip it
        return;
      }

      // Otherwise, add it to the mapping
      _fieldTypeMapping[fieldUID] = getFieldTypeFromTemplateType({
        fieldUID,
        fieldObj,
        isFieldListV2: true,
        plannerElementSetOfFields,
      });
    },
    {}
  );

  return _fieldTypeMapping;
};

export const getTextForClipboard = ({ content: rawContent }) => {
  const { output: content } = extractStateAndContent(rawContent);
  const htmlText = convertMarkdownToHTML({ content });

  const plainText = htmlToText(htmlText, {
    wordwrap: null,
  })?.trim();

  return { htmlText: replaceHTMLHeadings(htmlText), plainText };
};

export const getGlobalContextExplorationMap = ({ focusExplorations }) => {
  const explorationMap = {};

  _.forEach(focusExplorations, exploration => {
    const { id, label, associatedParents = [] } = exploration;

    const globalContext = _.find(associatedParents, {
      type: "MYP_GLOBAL_CONTEXT",
    });

    if (globalContext) {
      const globalContextId = globalContext.id;
      if (!explorationMap[globalContextId]) {
        explorationMap[globalContextId] = {
          id: globalContextId,
          label: globalContext.label,
          explorations: [],
        };
      }

      explorationMap[globalContextId].explorations.push({
        id,
        label,
      });
    }
  });

  return explorationMap;
};

export const safeToolArgumentsJSONParse = args => {
  try {
    const parsedArgs = JSON.parse(args);
    return parsedArgs;
  } catch (err) {
    // eslint-disable-next-line sonarjs/no-ignored-exceptions
    return args;
  }
};

export const mergeAndFilterAIMessages = messages => {
  const result = [];
  _.forEach(messages, message => {
    const parentId = _.get(message, "node.parentMessage.id");

    const existingMessageIndex = _.findIndex(
      result,
      msg => parentId && _.get(msg, "node.parentMessage.id") === parentId
    );

    if (
      existingMessageIndex !== -1 &&
      _.get(message, "node.authorType") === "assistant"
    ) {
      const existingMessage = result[existingMessageIndex];
      const mergedContent = _.join(
        [
          _.get(existingMessage, "node.content", ""),
          _.get(message, "node.content", ""),
        ],
        " "
      );

      // pick the message that is created after tool call
      const currentMessageHasToolCall = _.has(
        message,
        "node.metadata.toolCalls"
      );

      const messageId = currentMessageHasToolCall
        ? _.get(existingMessage, "node.id")
        : _.get(message, "node.id");

      result[existingMessageIndex] = update(existingMessage, {
        node: {
          id: { $set: messageId },
          content: {
            $set: mergedContent,
          },
        },
      });
    } else {
      result.push(message);
    }
  });

  return result;
};
